import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CropLandscapeIcon from "@mui/icons-material/CropLandscape";
import CropPortraitIcon from "@mui/icons-material/CropPortrait";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PermMediaIcon from "@mui/icons-material/PermMediaOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { tokens } from "../theme";
import AssetsPicker from "./AssetsPicker";
import type { AssetSummary } from "../http/foyer";
import type { DayOfWeek } from "@shared/types/foyer";
import type { SignageCampaign, SignageItem } from "@shared/types/signage";
import type { SignageScreen } from "../utils/signageScreens";
import {
  ORIENTATION_LABELS,
  durationLabel,
  type MediaOrientation,
} from "@shared/media";
import { byUrl, itemFromAsset, thumbnailFor } from "../utils/signageItems";

/**
 * Create or edit a campaign.
 *
 * A campaign is media, a recurrence rule, and the screens it runs on. The rule
 * is stored exactly as it is drawn here - local dates, local `HH:mm`, a weekday
 * set - and turned into instants at resolve time against the location's
 * timezone. Storing resolved instants instead would drift the whole booking by
 * an hour at every DST boundary.
 *
 * The screen list is NOT part of the campaign record. A campaign is owned by a
 * layer; which screens run it is stored per screen, in
 * `SignageAssignment.campaignIds`. That is what lets one chain-level campaign
 * be booked by four of a customer's twelve locations without four copies of it.
 */

const DAYS: { key: DayOfWeek; label: string }[] = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

/** Offered display times. The portal's list, which operators already know. */
const DISPLAY_SECONDS = [5, 10, 15, 20, 30, 45, 60];

const DEFAULT_ITEM_SECONDS = 10;
export const NAME_MAX = 100;

/** `00:00`-`00:00` is how the model spells all day. */
const ALL_DAY = { start: "00:00", end: "00:00" };

export interface SignageCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  /** Absent = creating a new one. */
  campaign?: SignageCampaign | null;
  /** Signage screens in scope - what the campaign can be booked onto. */
  screens: SignageScreen[];
  /** Screens already running this campaign, by id. */
  bookedScreenIds: string[];
  /** The library, for media thumbnails. */
  assets: AssetSummary[];
  onSave: (
    patch: Partial<SignageCampaign>,
    screenIds: string[]
  ) => Promise<void>;
  saving?: boolean;
  error?: string | null;
}

const FORMATS: MediaOrientation[] = ["landscape", "portrait", "custom"];

export default function SignageCampaignDialog({
  open,
  onClose,
  campaign,
  screens,
  bookedScreenIds,
  assets,
  onSave,
  saving,
  error,
}: SignageCampaignDialogProps) {
  const [name, setName] = useState("");
  const [items, setItems] = useState<SignageItem[]>([]);
  const [priority, setPriority] = useState<"normal" | "high">("normal");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [openEnded, setOpenEnded] = useState(true);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("20:00");
  const [allDay, setAllDay] = useState(false);
  const [days, setDays] = useState<DayOfWeek[]>([]);
  const [seconds, setSeconds] = useState(DEFAULT_ITEM_SECONDS);
  const [format, setFormat] = useState<MediaOrientation>("landscape");
  const [picked, setPicked] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const bookedKey = bookedScreenIds.join(",");

  useEffect(() => {
    if (!open) return;
    setName(campaign?.name ?? "");
    setItems(campaign?.items ?? []);
    setPriority(campaign?.priority ?? "normal");
    setStartDate(campaign?.startDate ?? "");
    setEndDate(campaign?.endDate ?? "");
    setOpenEnded(!campaign?.endDate);
    setDays(campaign?.days ?? []);
    setPicked(bookedScreenIds);

    const s = campaign?.startTime ?? ALL_DAY.start;
    const e = campaign?.endTime ?? ALL_DAY.end;
    const whole = s === e;
    setAllDay(campaign ? whole : false);
    setStartTime(whole ? "08:00" : s);
    setEndTime(whole ? "20:00" : e);

    setSeconds(campaign?.items[0]?.durationSeconds || DEFAULT_ITEM_SECONDS);

    // Open on the format the booked screens already are, so editing does not
    // silently re-scope a portrait campaign to the landscape tab.
    const booked = screens.filter((x) => bookedScreenIds.includes(x.id));
    setFormat(booked[0]?.orientation ?? "landscape");
    // Keyed on the CONTENT of the booked list, not its identity: the caller
    // rebuilds that array every render, and depending on it directly would
    // re-seed on each one - wiping a screen the operator had just ticked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign, bookedKey, screens]);

  const library = useMemo(() => byUrl(assets), [assets]);

  /** How many screens each format has, for the three cards. */
  const countByFormat = useMemo(() => {
    const out: Record<MediaOrientation, number> = {
      landscape: 0,
      portrait: 0,
      custom: 0,
    };
    for (const s of screens) if (s.orientation) out[s.orientation] += 1;
    return out;
  }, [screens]);

  /*
   * Screens of the chosen shape - PLUS the ones whose shape nobody knows yet.
   *
   * Content built for one orientation looks wrong on the other, so booking a
   * 16:9 campaign onto a known portrait totem is almost always a mistake, and
   * that guardrail stays: a screen whose shape we DO know never appears under
   * the wrong format.
   *
   * ⚠️ It used to exclude unshaped screens too, on the reasoning that listing
   * them "would be a guess, and a guess here books a real screen". That
   * reversed the cost. A screen only reports a resolution after it has checked
   * in, so the rule made an entire campaign unbookable until the hardware was
   * hung and talking - while the content PLAN for the very same screen could
   * be authored freely. Two pickers over one list of screens, disagreeing, and
   * only one of them saying why.
   *
   * The stakes decide the default, and they are not the stakes of the linear-
   * channel rule next door: an advert over live broadcast is a rights breach
   * nobody can take back, so that one refuses on missing information. A
   * campaign on a screen that turns out to be portrait looks wrong until
   * somebody changes it. Refusing on no information is the more expensive
   * mistake here, so an unknown shape is offered and LABELLED.
   */
  const listed = useMemo(
    () => screens.filter((s) => s.orientation === format || !s.orientation),
    [screens, format]
  );

  /** Signage screens that have never said what shape they are. */
  const unshaped = useMemo(
    () => screens.filter((s) => !s.orientation).length,
    [screens]
  );

  const listedIds = listed.map((s) => s.id);
  const selectedHere = picked.filter((id) => listedIds.includes(id));
  const allSelected =
    listed.length > 0 && selectedHere.length === listed.length;

  const toggleScreen = (id: string) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setPicked((prev) =>
      allSelected
        ? prev.filter((id) => !listedIds.includes(id))
        : [...new Set([...prev, ...listedIds])]
    );

  const addAsset = (asset: AssetSummary) => {
    setItems((prev) => [...prev, itemFromAsset(asset, prev.length, seconds)]);
    setPickerOpen(false);
  };

  /** Every reason this campaign is not ready, in the order they appear above. */
  const missing = [
    name.trim() ? null : "a name",
    items.length > 0 ? null : "media",
    picked.length > 0 ? null : "at least one screen",
  ].filter(Boolean) as string[];
  const ready = missing.length === 0;

  const submit = () =>
    void onSave(
      {
        name: name.trim(),
        // The display time is a campaign-level control in the design, and the
        // model holds duration per item - so it is written to every item
        // rather than kept as a fourth place a duration can live.
        items: items.map((i) => ({ ...i, durationSeconds: seconds })),
        enabled: true,
        priority,
        startDate: startDate || null,
        endDate: openEnded ? null : endDate || null,
        days,
        startTime: allDay ? ALL_DAY.start : startTime,
        endTime: allDay ? ALL_DAY.end : endTime,
      },
      picked
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogContent sx={{ p: 4 }}>
        <Stack direction="row" alignItems="flex-start" gap={3} sx={{ mb: 3 }}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: "8px",
              bgcolor: tokens.brandTintSoft,
              color: tokens.brand,
              display: "grid",
              placeItems: "center",
            }}
          >
            <AddIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: 24, fontWeight: 600, color: tokens.ink }}
            >
              {campaign ? "Edit campaign" : "Create campaign"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
              Media that interrupts the background loop on a schedule.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 4 }} />

        <Stack direction={{ xs: "column", lg: "row" }} gap={5}>
          {/* ---------------------------------------------------- the form */}
          <Stack gap={5} sx={{ flex: 1, minWidth: 0 }}>
            <Section
              title="Campaign name"
              hint="Give the campaign a name that makes it easy to recognise later."
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Name the campaign…"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
                inputProps={{ "data-testid": "campaign-name" }}
                InputProps={{
                  endAdornment: (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: tokens.inkMuted,
                        whiteSpace: "nowrap",
                        pl: 1,
                      }}
                    >
                      {name.length} / {NAME_MAX}
                    </Typography>
                  ),
                }}
              />
            </Section>

            <Section
              title="Screen format"
              hint="Choose the kind of screen this campaign runs on."
            >
              <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                {FORMATS.map((f) => (
                  <FormatCard
                    key={f}
                    format={f}
                    count={countByFormat[f]}
                    selected={format === f}
                    onClick={() => setFormat(f)}
                  />
                ))}
              </Stack>
            </Section>

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography sx={{ fontSize: 14, color: tokens.ink }}>
                  Choose which {ORIENTATION_LABELS[format].toLowerCase()}{" "}
                  screens take part.
                </Typography>
                <Stack direction="row" alignItems="center" gap={3}>
                  <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
                    {selectedHere.length} of {listed.length} selected
                  </Typography>
                  <Button
                    size="small"
                    onClick={toggleAll}
                    disabled={listed.length === 0}
                  >
                    {allSelected ? "Clear all" : "Select all"}
                  </Button>
                </Stack>
              </Stack>

              {listed.length === 0 ? (
                <Box
                  sx={{
                    border: `1px dashed ${tokens.divider}`,
                    borderRadius: "8px",
                    p: 3,
                    textAlign: "center",
                  }}
                >
                  {/*
                    THREE reasons this list is empty, and they need different
                    people to do different things. One sentence covering all of
                    them - the old "a screen appears once it has checked in" -
                    is the swallowed reason again: it named the cause an
                    operator cannot act on and hid the one they can.
                  */}
                  <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                    {screens.length === 0
                      ? "No screen here has the Signage role yet. A campaign runs on lobby displays, so set a screen's role to Signage under Screens first."
                      : unshaped > 0
                        ? `${unshaped} signage ${
                            unshaped === 1 ? "screen has" : "screens have"
                          } not reported a resolution yet, so nothing knows what shape ${
                            unshaped === 1 ? "it is" : "they are"
                          }. They appear here after checking in once.`
                        : `No ${ORIENTATION_LABELS[
                            format
                          ].toLowerCase()} signage screens here - the ones at this location are a different shape.`}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${tokens.divider}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  {listed.map((s, i) => {
                    const on = picked.includes(s.id);
                    return (
                      <Stack
                        key={s.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        onClick={() => toggleScreen(s.id)}
                        sx={{
                          height: 56,
                          px: 1.5,
                          cursor: "pointer",
                          borderTop:
                            i === 0 ? "none" : `1px solid ${tokens.divider}`,
                          bgcolor: on ? tokens.brandTintSoft : "transparent",
                          "&:hover": { bgcolor: tokens.brandTintSoft },
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={1.75}>
                          <TvOutlinedIcon
                            sx={{ fontSize: 20, color: tokens.inkMuted }}
                          />
                          <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                              {s.name}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 12, color: tokens.inkMuted }}
                            >
                              {s.premiseName || s.premiseId}
                              {/* Said out loud, because it is the one row on
                                  this list whose shape is not a fact. */}
                              {!s.orientation && " · shape not known yet"}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={2}>
                          <Typography
                            sx={{ fontSize: 12, color: tokens.inkMuted }}
                          >
                            {s.resolution}
                          </Typography>
                          <Checkbox
                            size="small"
                            checked={on}
                            inputProps={{
                              "aria-label": `Include ${s.name}`,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleScreen(s.id)}
                          />
                        </Stack>
                      </Stack>
                    );
                  })}
                </Box>
              )}
            </Box>

            <Section
              title="Schedule"
              hint="Choose the dates, times and days this campaign runs."
            >
              <Stack gap={3}>
                <Stack direction={{ xs: "column", md: "row" }} gap={3}>
                  <Stack gap={1} sx={{ flex: 1 }}>
                    <Label>Date range</Label>
                    <Stack direction="row" alignItems="center" gap={2}>
                      <TextField
                        size="small"
                        type="date"
                        label="From"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="Until"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={openEnded}
                        sx={{ flex: 1 }}
                      />
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          size="small"
                          checked={openEnded}
                          onChange={(e) => setOpenEnded(e.target.checked)}
                          inputProps={{ "aria-label": "No end date" }}
                        />
                        <Typography
                          sx={{ fontSize: 12, color: tokens.inkMuted }}
                        >
                          No end date
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>

                  <Stack gap={1}>
                    <Label>Days</Label>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {DAYS.map((d) => {
                        const on = days.includes(d.key);
                        return (
                          <Box
                            key={d.key}
                            role="button"
                            aria-pressed={on}
                            aria-label={d.label}
                            tabIndex={0}
                            onClick={() =>
                              setDays((prev) =>
                                prev.includes(d.key)
                                  ? prev.filter((x) => x !== d.key)
                                  : [...prev, d.key]
                              )
                            }
                            sx={{
                              px: 2,
                              py: 0.5,
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: 12,
                              fontWeight: 600,
                              border: `1px solid ${
                                on ? tokens.brand : tokens.divider
                              }`,
                              bgcolor: on
                                ? tokens.brandTintSoft
                                : "transparent",
                              color: on ? tokens.brand : tokens.inkMuted,
                            }}
                          >
                            {d.label}
                          </Box>
                        );
                      })}
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                      {days.length === 0
                        ? "No days picked - runs every day."
                        : `Runs on ${days.length} ${
                            days.length === 1 ? "day" : "days"
                          }.`}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack gap={1} sx={{ maxWidth: 560 }}>
                  <Label>Time of day</Label>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <TextField
                      size="small"
                      type="time"
                      label="From"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={allDay}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      type="time"
                      label="Until"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={allDay}
                      sx={{ flex: 1 }}
                    />
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        size="small"
                        checked={allDay}
                        onChange={(e) => setAllDay(e.target.checked)}
                        inputProps={{ "aria-label": "All day" }}
                      />
                      <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                        All day
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Section>

            <Section
              title="Choose media"
              hint="The image or clip this campaign shows."
            >
              {items.length === 0 ? (
                <Box
                  sx={{
                    border: `1px solid ${tokens.divider}`,
                    borderRadius: "8px",
                    py: 5,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "6px",
                        bgcolor: tokens.brandTintSoft,
                        color: tokens.brand,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <PermMediaIcon />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        No media chosen
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => setPickerOpen(true)}
                        data-testid="campaign-choose-media"
                        sx={{ px: 0 }}
                      >
                        Choose media
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              ) : (
                <Stack gap={1}>
                  {items.map((it, i) => {
                    const asset = library.get(it.assetUrl);
                    const thumb = thumbnailFor(asset);
                    return (
                      <Stack
                        key={it.id}
                        direction="row"
                        alignItems="center"
                        gap={1.5}
                        sx={{
                          p: 1,
                          border: `1px solid ${tokens.divider}`,
                          borderRadius: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 40,
                            flexShrink: 0,
                            borderRadius: "6px",
                            bgcolor: tokens.rowAlt,
                            overflow: "hidden",
                            display: "grid",
                            placeItems: "center",
                          }}
                        >
                          {/* A clip shows its captured poster - its own URL in
                              an <img> renders nothing. */}
                          {thumb ? (
                            <Box
                              component="img"
                              src={thumb}
                              alt=""
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <PermMediaIcon
                              sx={{ fontSize: 18, color: tokens.inkFaint }}
                            />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: 13 }}>
                            {asset?.originalName ??
                              it.assetUrl.split("/").pop()}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: tokens.inkMuted }}
                          >
                            {it.kind === "video" ? "Clip" : "Image"}
                            {asset?.orientation
                              ? ` · ${ORIENTATION_LABELS[asset.orientation]}`
                              : ""}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          aria-label="Remove media"
                          onClick={() =>
                            setItems((prev) => prev.filter((_, xi) => xi !== i))
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    );
                  })}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setPickerOpen(true)}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Add media
                  </Button>
                </Stack>
              )}
            </Section>

            <Section title="Display settings">
              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={3}
                alignItems={{ sm: "center" }}
              >
                <Box sx={{ width: 218 }}>
                  <Label>Display time</Label>
                  <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                    How long the campaign holds the screen each time.
                  </Typography>
                </Box>
                <TextField
                  select
                  size="small"
                  value={seconds}
                  onChange={(e) => setSeconds(Number(e.target.value))}
                  sx={{ width: 140 }}
                  inputProps={{ "data-testid": "campaign-seconds" }}
                >
                  {DISPLAY_SECONDS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s} seconds
                    </MenuItem>
                  ))}
                </TextField>

                <Divider orientation="vertical" flexItem />

                <Box sx={{ width: 234 }}>
                  <Label>Priority</Label>
                  <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                    High-priority campaigns come round about twice as often.
                  </Typography>
                </Box>
                <Stack direction="row" gap={1}>
                  {(["normal", "high"] as const).map((p) => (
                    <Box
                      key={p}
                      role="button"
                      aria-pressed={priority === p}
                      tabIndex={0}
                      onClick={() => setPriority(p)}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 500,
                        border: `1px solid ${
                          priority === p ? tokens.brand : tokens.divider
                        }`,
                        bgcolor:
                          priority === p ? tokens.brandTintSoft : "transparent",
                        color: priority === p ? tokens.brand : tokens.ink,
                      }}
                    >
                      {p === "high" ? "High priority" : "Normal"}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Section>
          </Stack>

          {/* ------------------------------------------------- the preview */}
          <Box
            sx={{
              width: { xs: "100%", lg: 356 },
              flexShrink: 0,
              borderLeft: { lg: `1px solid ${tokens.divider}` },
              pl: { lg: 4 },
            }}
          >
            <Typography
              sx={{ fontSize: 18, fontWeight: 600, color: tokens.ink }}
            >
              Preview
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mb: 2 }}>
              What you are about to create.
            </Typography>

            <PreviewMedia items={items} library={library} seconds={seconds} />

            <Box
              sx={{
                border: `1px solid ${tokens.divider}`,
                borderRadius: "8px",
                px: 1.5,
                mt: 3,
              }}
            >
              <SummaryRow label="Name" value={name || "Not set"} />
              <SummaryRow
                label="Screen format"
                value={ORIENTATION_LABELS[format]}
              />
              <SummaryRow
                label="Media"
                value={
                  items.length === 0
                    ? "Not chosen"
                    : `${items.length} ${items.length === 1 ? "item" : "items"}`
                }
              />
              <SummaryRow label="Display time" value={`${seconds} seconds`} />
              <SummaryRow
                label="Dates"
                value={
                  startDate
                    ? `${startDate} – ${openEnded ? "no end" : endDate || "no end"}`
                    : "Immediately"
                }
              />
              <SummaryRow
                label="Days"
                value={
                  days.length === 0
                    ? "Every day"
                    : DAYS.filter((d) => days.includes(d.key))
                        .map((d) => d.label)
                        .join(", ")
                }
              />
              <SummaryRow
                label="Time"
                value={allDay ? "All day" : `${startTime} – ${endTime}`}
              />
              <SummaryRow
                label="Priority"
                value={priority === "high" ? "High priority" : "Normal"}
                accent={priority === "high"}
              />
              <SummaryRow
                label="Screens"
                value={`${picked.length} ${
                  picked.length === 1 ? "screen" : "screens"
                }`}
                last
              />
            </Box>

            {/*
              Says what is still missing, not just that something is.
              "Fill in all the information" leaves an operator hunting up a
              form this tall for the one field they skipped.
            */}
            <Stack
              direction="row"
              gap={1.5}
              sx={{
                mt: 3,
                p: 1.75,
                borderRadius: "8px",
                border: `1px solid ${ready ? tokens.success : tokens.warning}`,
                bgcolor: ready ? tokens.successTint : tokens.warningTint,
              }}
            >
              {ready ? (
                <CheckCircleIcon sx={{ fontSize: 20, color: tokens.success }} />
              ) : (
                <ErrorOutlineIcon
                  sx={{ fontSize: 20, color: tokens.warning }}
                />
              )}
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: ready ? tokens.success : tokens.warning,
                  }}
                >
                  {ready ? "Ready to create." : "Not ready yet."}
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                  {ready
                    ? "Everything this campaign needs is set."
                    : `Still needs ${missing.join(", ")}.`}
                </Typography>
              </Box>
            </Stack>

            {error && (
              <Typography sx={{ fontSize: 13, color: tokens.error, mt: 2 }}>
                {error}
              </Typography>
            )}
          </Box>
        </Stack>

        <Divider sx={{ mt: 4, mb: 3 }} />

        <Stack direction="row" justifyContent="space-between">
          <Button onClick={onClose} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!ready || saving}
            onClick={submit}
            endIcon={<AddIcon />}
            data-testid="campaign-submit"
          >
            {saving
              ? "Saving…"
              : campaign
                ? "Save campaign"
                : "Create campaign"}
          </Button>
        </Stack>
      </DialogContent>

      <AssetsPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(_url, asset) => addAsset(asset)}
      />
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: tokens.ink }}>
        {title}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mb: 2 }}>
          {hint}
        </Typography>
      )}
      {!hint && <Box sx={{ mb: 2 }} />}
      {children}
    </Box>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.ink }}>
      {children}
    </Typography>
  );
}

function FormatCard({
  format,
  count,
  selected,
  onClick,
}: {
  format: MediaOrientation;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  const Icon = format === "portrait" ? CropPortraitIcon : CropLandscapeIcon;
  return (
    <Box
      role="button"
      aria-pressed={selected}
      tabIndex={0}
      onClick={onClick}
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: "8px",
        cursor: "pointer",
        border: `1px solid ${selected ? tokens.brand : tokens.divider}`,
        bgcolor: selected ? tokens.brandTintSoft : "transparent",
      }}
    >
      <Box
        sx={{
          p: 1.25,
          borderRadius: "8px",
          bgcolor: tokens.brandTintSoft,
          color: tokens.brand,
          display: "grid",
          placeItems: "center",
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {ORIENTATION_LABELS[format]}
        </Typography>
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {count} {count === 1 ? "screen" : "screens"}
        </Typography>
      </Box>
      <Checkbox
        size="small"
        checked={selected}
        onChange={onClick}
        inputProps={{ "aria-label": ORIENTATION_LABELS[format] }}
      />
    </Box>
  );
}

function PreviewMedia({
  items,
  library,
  seconds,
}: {
  items: SignageItem[];
  library: Map<string, AssetSummary>;
  seconds: number;
}) {
  const first = items[0];
  const thumb = first ? thumbnailFor(library.get(first.assetUrl)) : null;

  return (
    <Box
      sx={{
        position: "relative",
        aspectRatio: "426 / 208",
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: tokens.rowAlt,
        display: "grid",
        placeItems: "center",
      }}
    >
      {thumb ? (
        <Box
          component="img"
          src={thumb}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          No media chosen
        </Typography>
      )}
      {first && (
        <Typography
          sx={{
            position: "absolute",
            right: 8,
            bottom: 8,
            px: 0.75,
            py: 0.25,
            borderRadius: "4px",
            bgcolor: "rgba(0,0,0,0.6)",
            color: "#fff",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {durationLabel(seconds)}
        </Typography>
      )}
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  accent,
  last,
}: {
  label: string;
  value: string;
  accent?: boolean;
  last?: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        py: 1.5,
        gap: 2,
        borderBottom: last ? "none" : `1px solid ${tokens.divider}`,
      }}
    >
      <Typography
        sx={{ fontSize: 12, fontWeight: 600, color: tokens.inkMuted }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          textAlign: "right",
          color: accent ? tokens.brand : tokens.inkMuted,
          fontWeight: accent ? 600 : 400,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
